import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Button, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { Search, Eye, Trash2, Phone, Mail, User, Film, MessageSquare, ArrowLeft, Save } from 'lucide-react';
import CryptoJS from 'crypto-js';
import Swal from 'sweetalert2';
import { getAdminLeads, updateLeadFollowup, deleteAdminLead } from '../../services/adminApi';
import MetaTags from '../../components/MetaTags';
import './AdminLeadsList.css';

// 🔓 Frontend Decrypt Helper
const decryptContact = (text) => {
    if (!text || text === 'N/A') return 'N/A';
    if (!text.startsWith('U2FsdGVkX1')) return text;
    try {
        const secrets = ['SecretKey123', 'MySecretKey2026', 'BookMyAuditionSecret', 'REACT_APP_CRYPTO_SECRET'];
        for (const sec of secrets) {
            try {
                const bytes = CryptoJS.AES.decrypt(text, sec);
                const dec = bytes.toString(CryptoJS.enc.Utf8);
                if (dec && dec.length >= 8) return dec;
            } catch(e){}
        }
        return text;
    } catch(e) {
        return text;
    }
};

const AdminLeadsList = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [followupFilter, setFollowupFilter] = useState('All');
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 });

    // View Mode: 'LIST' or 'EDIT'
    const [viewMode, setViewMode] = useState('LIST');

    // Editable Lead Form State
    const [formData, setFormData] = useState({
        id: '',
        candidate_name: '',
        candidate_email: '',
        candidate_mobile: '',
        role_name: '',
        project_name: '',
        order_id: '',
        amount: '',
        lead_status: 'PAYMENT_PENDING',
        followup_status: 'NEW',
        admin_notes: ''
    });

    const [updating, setUpdating] = useState(false);

    const fetchLeads = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await getAdminLeads({
                page,
                search,
                status: statusFilter,
                followup_status: followupFilter,
                limit: 10
            });

            if (response.data.success) {
                const rawLeads = response.data.data;
                const leadMap = new Map();

                rawLeads.forEach((item) => {
                    const roleKey = `${String(item.role_name || '').trim().toLowerCase()}_${String(item.project_name || '').trim().toLowerCase()}_${Math.round(item.amount || 0)}`;

                    if (!leadMap.has(roleKey)) {
                        leadMap.set(roleKey, { 
                            ...item,
                            candidate_mobile: decryptContact(item.candidate_mobile),
                            candidate_email: decryptContact(item.candidate_email)
                        });
                    } else {
                        const existing = leadMap.get(roleKey);

                        if (item.order_id && item.order_id.startsWith('order_')) {
                            existing.order_id = item.order_id;
                        }
                        if (item.candidate_name && item.candidate_name !== 'Candidate' && existing.candidate_name === 'Candidate') {
                            existing.candidate_name = item.candidate_name;
                        }
                        if (item.candidate_email && item.candidate_email !== 'N/A' && existing.candidate_email === 'N/A') {
                            existing.candidate_email = decryptContact(item.candidate_email);
                        }
                        const cleanMobile = decryptContact(item.candidate_mobile);
                        if (cleanMobile && cleanMobile !== 'N/A' && !cleanMobile.startsWith('U2FsdGVkX1')) {
                            existing.candidate_mobile = cleanMobile;
                        }
                        if (item.lead_status === 'CONVERTED' || (item.lead_status === 'PAYMENT_FAILED' && existing.lead_status === 'PAYMENT_PENDING')) {
                            existing.lead_status = item.lead_status;
                        }

                        leadMap.set(roleKey, existing);
                    }
                });

                const cleanLeads = Array.from(leadMap.values());

                setLeads(cleanLeads);
                setPagination({
                    currentPage: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    total: cleanLeads.length
                });
            }
        } catch (err) {
            console.error("Error loading leads:", err);
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, followupFilter]);

    useEffect(() => {
        fetchLeads(1);
    }, [fetchLeads]);

    // Open Edit Form View
    const handleEditClick = (lead) => {
        setFormData({
            id: lead.id,
            candidate_name: lead.candidate_name || '',
            candidate_email: lead.candidate_email || '',
            candidate_mobile: lead.candidate_mobile || '',
            role_name: lead.role_name || '',
            project_name: lead.project_name || '',
            order_id: lead.order_id || '',
            amount: lead.amount || 0,
            lead_status: lead.lead_status || 'PAYMENT_PENDING',
            followup_status: lead.followup_status || 'NEW',
            admin_notes: lead.admin_notes || ''
        });
        setViewMode('EDIT');
    };

    // Handle Form Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Save Updated Lead
    const handleSaveLead = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            await updateLeadFollowup({
                leadId: formData.id,
                candidate_name: formData.candidate_name,
                candidate_email: formData.candidate_email,
                candidate_mobile: formData.candidate_mobile,
                role_name: formData.role_name,
                project_name: formData.project_name,
                order_id: formData.order_id,
                amount: formData.amount,
                lead_status: formData.lead_status,
                followup_status: formData.followup_status,
                admin_notes: formData.admin_notes
            });

            Swal.fire({
                icon: 'success',
                title: 'Lead Updated!',
                text: 'Lead details and call notes updated successfully.',
                background: '#121212',
                color: '#fff',
                confirmButtonColor: '#e10600'
            });

            setViewMode('LIST');
            fetchLeads(pagination.currentPage);
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: err.response?.data?.message || 'Failed to update lead details.',
                background: '#121212',
                color: '#fff',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = (leadId) => {
        Swal.fire({
            title: 'Delete Lead?',
            text: 'Are you sure you want to delete this lead record?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#1f2937',
            confirmButtonText: 'Yes, Delete',
            background: '#121212',
            color: '#ffffff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteAdminLead(leadId);
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Lead deleted successfully.',
                        background: '#121212',
                        color: '#fff',
                        confirmButtonColor: '#10b981'
                    });
                    fetchLeads(pagination.currentPage);
                } catch (err) {
                    console.error(err);
                }
            }
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'CONVERTED':
                return <Badge bg="success" className="px-2 py-1">Converted</Badge>;
            case 'PAYMENT_FAILED':
                return <Badge bg="danger" className="px-2 py-1">Payment Failed</Badge>;
            case 'PAYMENT_PENDING':
                return <Badge bg="warning" className="text-dark px-2 py-1">Payment Pending</Badge>;
            default:
                return <Badge bg="secondary" className="px-2 py-1">{status || 'Initiated'}</Badge>;
        }
    };

    return (
        <div className="admin-leads-page p-3">
            <MetaTags title="Lead Management - Admin" />

            {/* IF VIEW MODE IS 'EDIT', RENDER FULL DEDICATED EDIT FORM PAGE */}
            {viewMode === 'EDIT' ? (
                <div className="lead-edit-view">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => setViewMode('LIST')}
                                className="mb-2 btn-back-leads d-inline-flex align-items-center gap-1"
                            >
                                <ArrowLeft size={16} /> Back to Leads List
                            </Button>
                            <h4 className="text-white fw-bold text-uppercase m-0">
                                Edit <span style={{ color: '#E10600' }}>Lead Details</span>
                            </h4>
                            <small className="text-muted">Modify candidate information, payment status, and follow-up notes.</small>
                        </div>
                    </div>

                    <Form onSubmit={handleSaveLead}>
                        <Row className="g-4">
                            {/* CANDIDATE INFO EDIT FORM CARD */}
                            <Col md={6}>
                                <Card className="bg-dark border-secondary p-3 shadow-lg h-100">
                                    <div className="lead-info-title text-uppercase fw-bold text-danger mb-3 d-flex align-items-center gap-2">
                                        <User size={16} color="#e10600" /> Candidate Information
                                    </div>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="lead-form-label">Candidate Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="candidate_name"
                                            value={formData.candidate_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter candidate full name..."
                                            className="lead-form-control shadow-none"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="lead-form-label">Email Address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="candidate_email"
                                            value={formData.candidate_email}
                                            onChange={handleInputChange}
                                            placeholder="Enter candidate email..."
                                            className="lead-form-control shadow-none"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="lead-form-label">Mobile Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="candidate_mobile"
                                            value={formData.candidate_mobile}
                                            onChange={handleInputChange}
                                            placeholder="Enter mobile number..."
                                            className="lead-form-control shadow-none"
                                        />
                                    </Form.Group>
                                </Card>
                            </Col>

                            {/* AUDITION & ORDER INFO EDIT FORM CARD */}
                            <Col md={6}>
                                <Card className="bg-dark border-secondary p-3 shadow-lg h-100">
                                    <div className="lead-info-title text-uppercase fw-bold text-danger mb-3 d-flex align-items-center gap-2">
                                        <Film size={16} color="#e10600" /> Booking & Order Details
                                    </div>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Role Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="role_name"
                                                    value={formData.role_name}
                                                    onChange={handleInputChange}
                                                    placeholder="Role name..."
                                                    className="lead-form-control shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Project Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="project_name"
                                                    value={formData.project_name}
                                                    onChange={handleInputChange}
                                                    placeholder="Project name..."
                                                    className="lead-form-control shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={7}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Order ID</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="order_id"
                                                    value={formData.order_id}
                                                    onChange={handleInputChange}
                                                    placeholder="order_..."
                                                    className="lead-form-control font-mono shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={5}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Amount (₹)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="amount"
                                                    value={formData.amount}
                                                    onChange={handleInputChange}
                                                    placeholder="351.00"
                                                    className="lead-form-control shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="lead-form-label">Lead Payment Status</Form.Label>
                                        <Form.Select
                                            name="lead_status"
                                            value={formData.lead_status}
                                            onChange={handleInputChange}
                                            className="lead-form-select shadow-none"
                                        >
                                            <option value="PAYMENT_PENDING">⏳ PAYMENT_PENDING</option>
                                            <option value="PAYMENT_FAILED">❌ PAYMENT_FAILED</option>
                                            <option value="CONVERTED">✅ CONVERTED / SUCCESS</option>
                                            <option value="INITIATED">🚀 INITIATED</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Card>
                            </Col>

                            {/* FOLLOW-UP STATUS & REMARKS CARD */}
                            <Col md={12}>
                                <Card className="bg-dark border-secondary p-3 shadow-lg">
                                    <div className="lead-info-title text-uppercase fw-bold text-danger mb-3 d-flex align-items-center gap-2">
                                        <MessageSquare size={16} color="#e10600" /> Admin Call Notes & Follow-up
                                    </div>

                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Follow-up Status</Form.Label>
                                                <Form.Select
                                                    name="followup_status"
                                                    value={formData.followup_status}
                                                    onChange={handleInputChange}
                                                    className="lead-form-select shadow-none"
                                                >
                                                    <option value="NEW">🆕 NEW LEAD</option>
                                                    <option value="CONTACTED">📞 CONTACTED / CALLED CANDIDATE</option>
                                                    <option value="IN_PROGRESS">⏳ IN PROGRESS / WAITING FOR USER</option>
                                                    <option value="CLOSED">✅ CLOSED / CONVERTED</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={8}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="lead-form-label">Admin Remarks / Call History</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    name="admin_notes"
                                                    value={formData.admin_notes}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter call details, reason for payment failure, candidate responses..."
                                                    className="lead-form-control shadow-none"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end gap-3 mt-3 pt-3 border-top border-secondary">
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => setViewMode('LIST')}
                                            className="px-4"
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            variant="danger" 
                                            disabled={updating} 
                                            className="btn-bma-save px-4"
                                        >
                                            {updating ? <Spinner animation="border" size="sm" /> : <span className="d-flex align-items-center gap-2"><Save size={16}/> Save & Update Lead</span>}
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
            ) : (
                /* TABLE LIST VIEW */
                <div>
                    {/* HEADER */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h4 className="text-white fw-bold text-uppercase m-0">
                                Lead <span style={{ color: '#E10600' }}>Management</span>
                            </h4>
                            <small className="text-muted">Track candidate order IDs, abandoned checkouts & failed payments for follow-up.</small>
                        </div>
                    </div>

                    {/* FILTERS & SEARCH */}
                    <Card className="bg-dark border-secondary p-3 mb-4">
                        <Row className="g-3">
                            <Col md={5}>
                                <div className="lead-search-box">
                                    <Search size={16} className="lead-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, mobile, or order ID..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="lead-search-input"
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="bg-dark text-white border-secondary shadow-none"
                                >
                                    <option value="All">All Uncompleted Leads (Failed & Pending)</option>
                                    <option value="PAYMENT_FAILED">Failed Payments Only ❌</option>
                                    <option value="PAYMENT_PENDING">Payment Pending / Initiated ⏳</option>
                                    <option value="CONVERTED">Converted / Paid Success ✅</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Select
                                    value={followupFilter}
                                    onChange={(e) => setFollowupFilter(e.target.value)}
                                    className="bg-dark text-white border-secondary shadow-none"
                                >
                                    <option value="All">All Follow-up Statuses</option>
                                    <option value="NEW">New Lead</option>
                                    <option value="CONTACTED">Contacted</option>
                                    <option value="IN_PROGRESS">In Progress</option>
                                    <option value="CLOSED">Closed</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Card>

                    {/* TABLE LIST */}
                    <Card className="bg-dark border-secondary shadow-lg">
                        <div className="table-responsive">
                            <Table hover variant="dark" className="align-middle mb-0">
                                <thead>
                                    <tr className="text-uppercase small text-muted">
                                        <th>#</th>
                                        <th>Candidate Name</th>
                                        <th>Contact Info</th>
                                        <th>Role / Project</th>
                                        <th>Order ID</th>
                                        <th>Amount</th>
                                        <th>Lead Status</th>
                                        <th>Follow-up</th>
                                        <th className="leads-actions-header">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5">
                                                <Spinner animation="border" variant="danger" />
                                                <p className="text-muted mt-2 m-0">Loading leads...</p>
                                            </td>
                                        </tr>
                                    ) : leads.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5 text-muted">
                                                No leads found matching your criteria.
                                            </td>
                                        </tr>
                                    ) : (
                                        leads.map((lead, index) => (
                                            <tr key={lead.id}>
                                                <td className="text-muted">{(pagination.currentPage - 1) * 10 + index + 1}</td>
                                                <td className="fw-bold text-white">{lead.candidate_name}</td>
                                                <td className="small">
                                                    <div><Mail size={12} className="me-1 text-primary"/> {lead.candidate_email || 'N/A'}</div>
                                                    <div><Phone size={12} className="me-1 text-success"/> {lead.candidate_mobile || 'N/A'}</div>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold text-white">{lead.role_name || 'N/A'}</div>
                                                    <small className="text-muted">{lead.project_name || 'N/A'}</small>
                                                </td>
                                                <td className="small text-info font-mono">{lead.order_id || 'N/A'}</td>
                                                <td className="fw-bold text-success">₹{lead.amount || 0}</td>
                                                <td>{getStatusBadge(lead.lead_status)}</td>
                                                <td>
                                                    <span className={`badge ${lead.followup_status === 'CLOSED' ? 'bg-secondary' : 'bg-primary'} px-2 py-1`}>
                                                        {lead.followup_status || 'NEW'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="leads-actions-container">
                                                        <button
                                                            type="button"
                                                            className="lead-btn-action lead-btn-view"
                                                            title="Edit Lead Details & Notes"
                                                            onClick={() => handleEditClick(lead)}
                                                        >
                                                            <Eye size={14} color="#ffffff" />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="lead-btn-action lead-btn-delete"
                                                            title="Delete Lead"
                                                            onClick={() => handleDelete(lead.id)}
                                                        >
                                                            <Trash2 size={14} color="#ffffff" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default AdminLeadsList;